import type {ExtractPropsWithoutRef} from '@/types';
import type {HotelCategoryType} from '@travel-frontend/ui-components';

import styled from '@emotion/styled';
import {LabelMediumBold, PictureFallback} from '@travel-frontend/ui-components';
import {forwardRef} from 'react';

import {HotelCategory} from '@/components/HotelCategory';
import {Container} from '@/components/HotelTile/components/Container';
import {Image} from '@/components/HotelTile/components/Image';
import {PriceTag} from '@/components/PriceTag';
import {RatingBadge} from '@/modules/common/components/RatingBadge';
import {type DiscountType, type Price} from '@/redux/v1/models';
import {testId} from '@/utils/testId';

type Props = Omit<
  ExtractPropsWithoutRef<typeof Container>,
  | 'imageElement'
  | 'categoryElement'
  | 'nameElement'
  | 'ratingElement'
  | 'priceElement'
> & {
  imageUrl?: null | string;
  name: string;
  small?: boolean;
  category?: null | number;
  categoryType?: null | HotelCategoryType;
  ratingScore?: null | number;
  totalPrice: Price;
  totalPriceInternal?: Price | null;
  totalPriceOriginal: Price;
  totalDiscount: Price;
  totalDiscountOriginal?: Price;
  pricePerPerson: Price;
  discountType: DiscountType;
  discountPercentage: number;
  canShowPriceCache: boolean;
  totalDiscountHint?: string;
};

export const HotelTile = styled(
  forwardRef<HTMLDivElement, Props>(function HotelTile(
    {
      imageUrl,
      name,
      small = false,
      category,
      categoryType,
      ratingScore,
      totalPrice,
      totalPriceInternal,
      totalPriceOriginal,
      totalDiscount,
      totalDiscountOriginal,
      pricePerPerson,
      discountType,
      discountPercentage,
      totalDiscountHint,
      canShowPriceCache,
      ...restProps
    },
    ref
  ) {
    let ratingElement = <div />;
    if (ratingScore != null && ratingScore >= 0) {
      ratingElement = <RatingBadge ratingScore={ratingScore} />;
    }

    return (
      <Container
        {...restProps}
        ref={ref}
        small={small}
        imageElement={
          imageUrl ? <Image src={imageUrl} alt={name} /> : <PictureFallback />
        }
        categoryElement={
          <HotelCategory
            value={category}
            type={categoryType}
            data-testid={testId('hotel-category')}
          />
        }
        nameElement={
          <LabelMediumBold data-testid={testId('hotel-name')} truncate>
            {name}
          </LabelMediumBold>
        }
        ratingElement={ratingElement}
        priceElement={
          <PriceTag
            totalPrice={totalPrice}
            totalPriceInternal={totalPriceInternal}
            totalPriceOriginal={totalPriceOriginal}
            totalDiscount={totalDiscount}
            totalDiscountOriginal={totalDiscountOriginal}
            pricePerPerson={pricePerPerson}
            hasCompactNotation
            discountType={discountType}
            discountPercentage={discountPercentage}
            totalDiscountHint={totalDiscountHint}
            canShowPriceCache={canShowPriceCache}
          />
        }
      />
    );
  })
)``;
